import type { FC } from 'react';
import { clsx } from 'clsx';
import { formatCurrencyValue } from '@repo/common';
import AutoTextfit from '../../../../shared/components/AutoTextfit';
import Image from '../../../../shared/components/Image';
import { useTranslator } from '../../../../shared/hooks/useTranslations';
import { testIds } from '../../../../core/testIds';
import { JACKPOT_STATISTICS_SORT, JACKPOT_WIN_MINOR_UNITS } from '../../constants';
import type { IJackpotWinRow, JackpotStatisticsSortMode } from '../../types/jackpotStatistics.types';
import { isPremiumLinkJackpot } from '../../statisticsAssets';
import JackpotLevelLabel from './JackpotLevelLabel';
import styles from './JackpotStatistics.module.css';

const HEADER_MAX_FONT_SIZE_PX = 29;
const AMOUNT_MIN_FONT_SIZE_PX = 8;

interface JackpotStatisticsHeaderProps {
  jackpotName: string;
  /** The highlighted win, or `null` when the current filter matches no win at all. */
  win: IJackpotWinRow | null;
  /** Drives the caption: LAST 100 shows the biggest win, TOP 10 shows the last win. */
  sortMode: JackpotStatisticsSortMode;
  isLaunchBlocked: boolean;
  onSelectGame: (win: IJackpotWinRow) => void;
}

const JackpotStatisticsHeader: FC<JackpotStatisticsHeaderProps> = ({
  jackpotName,
  win,
  sortMode,
  isLaunchBlocked,
  onSelectGame,
}) => {
  const translate = useTranslator();

  // The caption names what the header is actually showing, and the value rules flip with the
  // ordering filter: LAST 100 → the jackpot's biggest win, TOP 10 → its most recent win.
  const caption =
    sortMode === JACKPOT_STATISTICS_SORT.TOP_10
      ? translate('statistics.last_win', 'Last Win')
      : translate('statistics.top_win', 'Top Win');

  // No win under the current filter (a level nobody has won yet): the band shrinks to the
  // caption alone, so the drawer keeps its shape and the list shows the empty state below.
  if (!win) {
    return (
      <div className={clsx(styles.headerPanel, styles.headerPanelEmpty)} data-testid={testIds.jackpots.statisticsHeader}>
        <span className={styles.headerCaption}>{caption}</span>
      </div>
    );
  }

  return (
    <div className={styles.headerPanel} data-testid={testIds.jackpots.statisticsHeader}>
      <button
        type="button"
        className={styles.headerThumb}
        disabled={isLaunchBlocked}
        aria-label={win.gameName}
        onClick={() => onSelectGame(win)}
      >
        <Image src={`/verticals/${win.gameKey}`} alt={win.gameName} className={styles.headerThumbImg} />
      </button>
      {/* Three stacked, left-aligned lines: caption, level wordmark, amount. */}
      <div className={styles.headerInfo} data-premium-link={isPremiumLinkJackpot(jackpotName)}>
        <span className={styles.headerCaption}>{caption}</span>
        <span className={styles.headerLevel}>
          <JackpotLevelLabel jackpotName={jackpotName} levelName={win.levelName} withVipBadge />
        </span>
        <span className={styles.headerAmount}>
          <AutoTextfit maxFontSize={HEADER_MAX_FONT_SIZE_PX} minFontSize={AMOUNT_MIN_FONT_SIZE_PX}>
            {formatCurrencyValue(win.amount, JACKPOT_WIN_MINOR_UNITS)}
            <span className={styles.headerCurrency}>{win.currency}</span>
          </AutoTextfit>
        </span>
      </div>
    </div>
  );
};

export default JackpotStatisticsHeader;
